import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useLanguageSync } from "../hooks/useLanguageNavigation";
import { LanguageCode, LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from "../utils/languageRouting";
import LanguageSelectionModal from "./LanguageSelectionModal";

interface LanguageSelectorProps {
  variant?: "default" | "compact" | "icon-only" | "modal";
  className?: string;
  usePrefix?: boolean; // true for /it/professional, false for /professional/it
}

/**
 * Language Selector Component
 * Allows users to change language and updates the URL automatically
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  variant = "default",
  className = "",
  usePrefix = true, // Default to prefix style (/it/professional)
}) => {
  const { changeLanguage, currentLanguage } = useLanguageSync();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = async (language: LanguageCode) => {
    await changeLanguage(language, { usePrefix });
    setIsOpen(false);
  };

  const currentLanguageName = LANGUAGE_NAMES[currentLanguage];
  const currentLanguageFlag = currentLanguage.toUpperCase();

  // Modal variant - opens full modal dialog with flags
  if (variant === "modal") {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm ${className}`}
        >
          <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
          <span className="font-medium text-gray-700 hidden sm:inline">{currentLanguageName}</span>
          <span className="font-medium text-gray-700 sm:hidden">{currentLanguageFlag}</span>
          <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
        </button>
        
        <LanguageSelectionModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          usePrefix={usePrefix}
        />
      </>
    );
  }

  // Icon-only variant
  if (variant === "icon-only") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1.5 sm:gap-2"
          aria-label="Select Language"
        >
          <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
          <span className="text-xs sm:text-sm font-medium text-gray-700">{currentLanguageFlag}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              Select Language
            </div>
            <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full px-3 py-2 text-left text-xs sm:text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    currentLanguage === lang ? "bg-blue-50 text-blue-600" : "text-gray-700"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{lang.toUpperCase()}</span>
                    <span className="hidden sm:inline">{LANGUAGE_NAMES[lang]}</span>
                  </span>
                  {currentLanguage === lang && (
                    <Check className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Compact variant
  if (variant === "compact") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
        >
          <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
          <span className="font-medium text-gray-700">{currentLanguageFlag}</span>
          <ChevronDown className={`h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-xs sm:text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    currentLanguage === lang ? "bg-blue-50 text-blue-600" : "text-gray-700"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-semibold w-6 sm:w-8">{lang.toUpperCase()}</span>
                    <span className="hidden sm:inline">{LANGUAGE_NAMES[lang]}</span>
                  </span>
                  {currentLanguage === lang && (
                    <Check className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors flex items-center gap-2 sm:gap-3 bg-white"
      >
        <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
        <span className="text-xs sm:text-sm font-medium text-gray-700">{currentLanguageName}</span>
        <ChevronDown className={`h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-3 sm:px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
            Select Language
          </div>
          <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  currentLanguage === lang ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className={`text-xs sm:text-sm font-semibold ${
                    currentLanguage === lang ? "text-blue-600" : "text-gray-600"
                  }`}>
                    {lang.toUpperCase()}
                  </span>
                  <span className={`text-xs sm:text-sm ${
                    currentLanguage === lang ? "text-blue-600 font-medium" : "text-gray-700"
                  }`}>
                    {LANGUAGE_NAMES[lang]}
                  </span>
                </div>
                {currentLanguage === lang && (
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
